import React from "react";
import { useParams, useHistory } from "@/compat/router";
import { makeStyles } from "@/compat/muiStyles";
import { Paper, Typography, CircularProgress, Button, GridLegacy as Grid, Box, IconButton, Table, TableBody, TableCell, TableRow, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import useAdviceDetails from "../hooks/queries/useAdviceDetails";
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import HealthAndSafetyIcon from '../components/icons/HealthAndSafetyIcon';
import DescriptionIcon from '@mui/icons-material/Description';
import GetAppIcon from '@mui/icons-material/GetApp';
import { formatShortMonthDateAndTime } from "../../utils/dateTimeUtils";
import SeekAdviceDialog from '../components/dialogs/SeekAdviceDialog';
import adviceService from "../services/adviceService";
import CloseIcon from "@mui/icons-material/Close";
import { alpha } from "@/compat/muiStyles";
import AttachFileIcon from '@mui/icons-material/AttachFile';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    header: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    loading: {
        display: "flex",
        justifyContent: "center",
        padding: theme.spacing(3),
    },
    label: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        fontSize: '20px',
        fontFamily: 'Inter, sans-serif',
    },
    value: {
        marginBottom: theme.spacing(2),
        border: '1px solid #E0E0E0',
        minHeight: '200px',
        padding: theme.spacing(2),
        color: theme.palette.text.mainOpaque,
        borderRadius: '8px',
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        cursor: 'pointer',
        color: theme.palette.tertiary.main,
        '& .MuiSvgIcon-root': {
            fontSize: '20px',
            display: 'flex',
        },
        '& .MuiTypography-root': {
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
        }
    },
    IconButton: {
        color: theme.palette.tertiary.main,
        backgroundColor: theme.palette.tertiary.background,
        cursor: 'default',
        width: '40px',
        height: '40px',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: theme.palette.tertiary.background,
            cursor: 'default'
        },
        '& .MuiSvgIcon-root': {
            fontSize: '16px'
        }
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
    },
  
    subTitle: {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        marginBottom: theme.spacing(2),
    },
    detailsTable: {
        borderCollapse: 'separate',
        borderSpacing: '0 8px',
    },
    tableCell: {
        border: 'none',
        padding: '0 16px 0 0',
        fontFamily: 'Inter, sans-serif',
        fontSize: '16px',
        fontWeight: 500,
        '&:first-of-type': {
            width: '1%',
            whiteSpace: 'nowrap'
        }
    },
    historyHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    historyDescription: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '16px',
        color: theme.palette.subText.text,
        marginBottom: theme.spacing(2),
    },
    seekAdviceButton: {
        backgroundColor: '#5c45c7',
        color: 'white',
        '&:hover': {
            backgroundColor: '#4a3aa0',
        },
    },
    fileDetails: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    fileIcon: {
        color: '#000',
        fontSize: '24px',
    },
    fileInfo: {
        display: 'flex',
        flexDirection: 'column',
    },
    fileName: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        fontFamily: 'Inter, sans-serif',
    },
    fileSize: {
        color: theme.palette.text.secondary,
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif',
    },
    historyItem: {
        marginBottom: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
        padding: theme.spacing(2),
        backgroundColor: '#fff',
    },
    historyItemHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1.5),
    },
    historyItemLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    historyItemContent: {
        display: 'flex',
        flexDirection: 'column',
    },
    historyAvatar: {
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#e5deff',
        color: '#1b0063',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        fontSize: '14px',
    },
    historyUserName: {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        fontSize: '16px',
        color: theme.palette.text.primary,
    },
    historyDate: {
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    historyChip: {
        borderRadius: '5px',
        backgroundColor: 'transparent',
        height: '24px',
        fontSize: '14px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        marginLeft: '10px',
        border: `1px solid ${theme.palette.divider}`,
    },
    downloadIcon: {
        color: theme.palette.tertiary.main,
        cursor: 'pointer',
        '&:hover': {
            color: theme.palette.tertiary.dark,
        },
    },
    attachmentItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
        padding: theme.spacing(1, 0),
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        '&:last-child': {
            borderBottom: 'none',
            marginBottom: 0,
        },
    },
    downloadButton: {
        padding: 8,
        marginTop: -4,
        marginBottom: -4,
    },
    adviceDialog: {
        '& .MuiDialog-paper': {
            borderRadius: '8px',
            overflowY: 'visible',
        },
        '& .MuiDialogContent-root': {
            padding: theme.spacing(3),
            paddingBottom: theme.spacing(2),
        },
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogTitle: {
        color: '#12054d',
        fontWeight: 600,
        fontFamily: 'Inter, sans-serif',
        fontSize: '20px',
        marginBottom: theme.spacing(0.5),
        paddingRight: 0,
    },
    dialogSubtitle: {
        color: '#12054db8',
        marginBottom: theme.spacing(3),
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
        fontWeight: 'normal',
    },
    commentsLabel: {
        color: '#666',
        marginBottom: theme.spacing(1),
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
        fontWeight: 500,
    },
    commentsText: {
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '8px',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        minHeight: '120px',
        width: '100%',
        overflowY: 'auto',
        backgroundColor: 'white',
        fontFamily: 'Inter, sans-serif',
    },
    attachmentsContainer: {
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '8px',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        backgroundColor: 'white',
    },
    attachmentsLabel: {
        color: '#666',
        marginBottom: theme.spacing(1),
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
        fontWeight: 500,
    },
    attachmentsList: {
        marginTop: 0,
    },
    fileItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1, 0),
        '&:not(:last-child)': {
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        },
        '&:first-of-type': {
            paddingTop: 0,
        },
        '&:last-child': {
            paddingBottom: 0,
        },
    },
    fileType: {
        color: theme.palette.text.secondary,
        fontSize: '12px',
        textTransform: 'uppercase',
        fontFamily: 'Inter, sans-serif',
    },
    dialogActions: {
        padding: theme.spacing(0),
        marginTop: theme.spacing(1),
        display: 'flex',
        justifyContent: 'flex-end',
    },
    cancelButton: {
        textTransform: 'none',
        color: '#6750A4',
        fontFamily: 'Inter, sans-serif',
    },
    attachmentIcon: {
        fontSize: '20px',
        color: '#6750A4',
    },
    attachmentIconContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '32px',
        height: '32px',
        borderRadius: '4px',
        border: '1px solid #E9E2F5',
    },
    section: {
        marginBottom: theme.spacing(3),
        '&:last-child': {
            marginBottom: 0,
        },
    },
}));

const AdviceDetail = () => {
    const classes = useStyles();
    const { id } = useParams();
    const history = useHistory();
    const { data: advice, isLoading, error } = useAdviceDetails(id);
    const [isSeekDialogOpen, setIsSeekDialogOpen] = React.useState(false);
    const [isDetailsDialogOpen, setIsDetailsDialogOpen] = React.useState(false);
    const [selectedHistoryId, setSelectedHistoryId] = React.useState(null);
    const [selectedAdvice, setSelectedAdvice] = React.useState(null);
    const [loadingDetails, setLoadingDetails] = React.useState(false);

    const handleCancel = () => {
        history.goBack();
    };

    const handleOpenSeekDialog = () => {
        setIsSeekDialogOpen(true);
    };

    const handleCloseSeekDialog = () => {
        setIsSeekDialogOpen(false);
    };

    const attachments = advice?.documentLinks?.map(link => ({
        id: link.contentDocument.id,
        name: link.contentDocument.title,
        fileType: link.contentDocument.fileType,
        fileExtension: link.contentDocument.fileExtension,
        title: link.contentDocument.title
    })) || [];

    const handleDownload = (contentDocumentId, filename) => {
        adviceService.downloadAdviceDocument(contentDocumentId, filename);
    };

    const adviceHistory = advice?.childEntries?.map(entry => {
        const name = entry.consultant || advice?.name || "";
        return {
            id: entry.id,
            name: name,
            date: formatShortMonthDateAndTime(new Date(entry.startTime)),
            desc: entry.description,
            initials: name.split(' ').map(n => n[0]).join(''),
            hasAttachments: entry.documentLinks && entry.documentLinks.length > 0
        };
    }) || [];

    const handleHistoryItemClick = async (historyId) => {
        setSelectedHistoryId(historyId);
        setLoadingDetails(true);
        
        try {
            // Fetch the specific advice entry details
            const response = await adviceService.fetchAdviceDetails(historyId);
            setSelectedAdvice(response);
            setIsDetailsDialogOpen(true);
        } catch (error) {
            console.error('Error fetching advice details:', error);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleCloseDetailsDialog = () => {
        setIsDetailsDialogOpen(false);
        setSelectedAdvice(null);
        setSelectedHistoryId(null);
    };

    if (isLoading) {
        return (
            <Paper className={classes.paper}>
                <div className={classes.loading}>
                    <CircularProgress />
                </div>
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper className={classes.paper}>
                <Typography color="error">
                    Error loading advice details. Please try again.
                </Typography>
            </Paper>
        );
    }

    return (
        <>
            <div className={classes.header}>
                <div className={classes.backButton} onClick={handleCancel}>
                    <ChevronLeft />
                    <Typography>Back to Listing</Typography>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <IconButton className={classes.IconButton}><HealthAndSafetyIcon /></IconButton><Typography className={classes.title} variant="h5">H&S Advice</Typography>
                </div>
            </div>           
            <Typography className={classes.subTitle} variant="h6">Advice Record</Typography>
            <Paper className={classes.paper}>
                <Table className={classes.detailsTable}>
                    <TableBody>
                        <TableRow>
                            <TableCell className={`${classes.tableCell}`}>
                                Consultant Responsible:
                            </TableCell>
                            <TableCell className={`${classes.tableCell}`}>
                                {advice?.name}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell className={`${classes.tableCell}`}>
                                Person Advice Initially Given To:
                            </TableCell>
                            <TableCell className={`${classes.tableCell}`}>
                                {advice?.contactName}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell className={`${classes.tableCell}`}>
                                Type:
                            </TableCell>
                            <TableCell className={`${classes.tableCell}`}>
                                {advice?.activityType}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell className={`${classes.tableCell}`}>
                                Site:
                            </TableCell>
                            <TableCell className={`${classes.tableCell}`}>
                                {advice?.accountName}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Paper>
            <Paper className={classes.paper}>
                <div className={classes.section}>
                    <Typography className={classes.label}>Description</Typography>
                    <Typography 
                        component="div" 
                        className={classes.value}
                        style={{ borderRadius: '8px' }}
                    >
                        {advice?.description}
                    </Typography>
                </div>

                <div className={classes.section}>
                    <Typography className={classes.label}>Attachments</Typography>
                    <div 
                        className={classes.value}
                        style={{ borderRadius: '8px' }}
                    >
                        {attachments.length > 0 ? (
                            attachments.map((attachment) => (
                                <div key={attachment.id} className={classes.attachmentItem}>
                                    <div className={classes.fileDetails}>
                                        <DescriptionIcon className={classes.fileIcon} />
                                        <div className={classes.fileInfo}>
                                            <Typography className={classes.fileName}>{attachment.name}</Typography>
                                            <Typography className={classes.fileSize}>{attachment.fileType}</Typography>
                                        </div>
                                    </div>
                                    <IconButton 
                                        size="small"
                                        className={classes.downloadButton}
                                        onClick={() => handleDownload(attachment.id, attachment.title)}
                                        aria-label="download file"
                                    >
                                        <GetAppIcon className={classes.downloadIcon} />
                                    </IconButton>
                                </div>
                            ))
                        ) : (
                            <Typography variant="body2">No attachments available</Typography>
                        )}
                    </div>
                </div>
            </Paper>
            <Paper className={classes.paper}>
                <div>
                    <div className={classes.historyHeader}>
                        <Typography className={classes.label}>
                            Advice History
                            <Chip 
                                label={`${adviceHistory.length} item${adviceHistory.length !== 1 ? 's' : ''}`}
                                className={classes.historyChip}
                                size="small"
                            />
                        </Typography>
                       
                        <Button
                            variant="contained"
                            className={classes.seekAdviceButton}
                            onClick={handleOpenSeekDialog}
                        >
                            Seek More Advice
                        </Button>
                    </div>

                    <Typography className={classes.historyDescription}>
                        Record and track advice.
                    </Typography>

                    {adviceHistory.map((item, index) => (
                        <div 
                            key={index} 
                            className={classes.historyItem}
                            onClick={() => handleHistoryItemClick(item.id)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className={classes.historyItemHeader}>
                                <div className={classes.historyItemLeft}>
                                    <div className={classes.historyAvatar}>
                                        {item.initials}
                                    </div>
                                    <div className={classes.historyItemContent}>
                                        <Typography className={classes.historyUserName}>{item.name}</Typography>
                                        <Typography className={classes.historyDate}>{item.date}</Typography>
                                    </div>
                                </div>
                                
                                {item.hasAttachments && (
                                    <div className={classes.attachmentIconContainer}>
                                        <AttachFileIcon className={classes.attachmentIcon} />
                                    </div>
                                )}
                            </div>
                            <Typography className={classes.historyDescription}>{item.desc}</Typography>
                        </div>
                    ))}
                </div>
            </Paper>
            <SeekAdviceDialog
                open={isSeekDialogOpen}
                onClose={handleCloseSeekDialog}
                adviceEntryId={id}
                titleText="Seek More Advice"
            />
            
            <Dialog 
                open={isDetailsDialogOpen}
                onClose={handleCloseDetailsDialog}
                fullWidth
                maxWidth="sm"
                className={classes.adviceDialog}
            >
                <DialogContent>
                    <IconButton 
                        className={classes.closeButton} 
                        onClick={handleCloseDetailsDialog}
                        aria-label="close"
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                    
                    <Typography className={classes.dialogTitle}>Advice</Typography>
                    
                    <Typography variant="body2" className={classes.dialogSubtitle}>
                        Read comments about this issue
                    </Typography>
                    
                    {loadingDetails ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                            <CircularProgress size={24} />
                        </div>
                    ) : selectedAdvice ? (
                        <>
                            <Typography className={classes.commentsLabel}>Comments</Typography>
                            <div className={classes.commentsText}>
                                {selectedAdvice.description}
                            </div>
                            
                            {selectedAdvice.documentLinks && selectedAdvice.documentLinks.length > 0 && (
                                <>
                                    <Typography className={classes.attachmentsLabel}>Attachments</Typography>
                                    <div className={classes.attachmentsContainer}>
                                        {selectedAdvice.documentLinks.map((link) => (
                                            <div key={link.contentDocument.id} className={classes.fileItem}>
                                                <div className={classes.fileDetails}>
                                                    <DescriptionIcon className={classes.fileIcon} />
                                                    <div className={classes.fileInfo}>
                                                        <Typography className={classes.fileName}>
                                                            {link.contentDocument.title}
                                                        </Typography>
                                                        <Typography className={classes.fileType}>
                                                            {link.contentDocument.fileType}
                                                        </Typography>
                                                    </div>
                                                </div>
                                                <IconButton
                                                    size="small"
                                                    className={classes.downloadButton}
                                                    onClick={() => handleDownload(
                                                        link.contentDocument.id, 
                                                        {
                                                            title: link.contentDocument.title,
                                                            fileType: link.contentDocument.fileType,
                                                            fileExtension: link.contentDocument.fileExtension
                                                        }
                                                    )}
                                                >
                                                    <GetAppIcon className={classes.downloadIcon} />
                                                </IconButton>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                            
                            <div className={classes.dialogActions}>
                                <Button 
                                    onClick={handleCloseDetailsDialog} 
                                    className={classes.cancelButton}
                                >
                                    Close
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Typography>No details available</Typography>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AdviceDetail; 
